import { Col, Row } from 'antd';
import Buttons from 'components/Global/Buttons';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import TimePickerField from 'components/Global/TimePicker';
import InputHoursField from 'components/InputHoursField';
import InputShiftHoursField from 'components/InputShiftHoursField';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useState } from 'react';
import {
	hasFormError,
	invalidText,
	minutesToTimeString,
	timeStringToMinutes,
	toastText,
	validateFormData,
} from 'utils/utils';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';
import { shiftMasterApi } from 'Api/shiftMaster';

interface ShiftDetails {
	shiftName: string;
	shiftType: string;
	startTime: Dayjs | null;
	endTime: Dayjs | null;
	defaultRegularHours: string;
	defaultOverTimeHours: string;
}

const AddShiftBody: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const { closeDrawerByAnimation, editSelected, fetchShiftMaster } = props;

	// Initialize shift details state
	const [shiftDetails, setShiftDetails] = useState<ShiftDetails>({
		shiftName: '',
		shiftType: '',
		startTime: null, // Changed to null initially
		endTime: null, // Changed to null initially
		defaultRegularHours: '', // Changed to empty string initially
		defaultOverTimeHours: '00:00',
	});

	const [formError, setFormError] = useState<any>({
		shiftName: false,
		shiftType: false,
		startTime: false,
		endTime: false,
		defaultRegularHours: false,
		defaultOverTimeHours: false,
	});

	const [hasError, setHasError] = useState(false);

	// Initialize form with edit data if available
	useEffect(() => {
		if (editSelected) {
			setShiftDetails({
				shiftName: editSelected.shiftName || '',
				shiftType: editSelected.shiftType || '',
				startTime: dayjs(editSelected.startTime),
				endTime: dayjs(editSelected.endTime),
				defaultRegularHours:
					minutesToTimeString(editSelected.defaultRegularHours) ||
					'08:00',
				defaultOverTimeHours:
					minutesToTimeString(editSelected.defaultOverTimeHours) ||
					'00:00',
			});
		}
	}, [editSelected]);

	const handleChange = (
		value: any,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		if (required) {
			setHasError(invalidText(value));
		}

		if (name === 'name' && typeof value === 'string') {
			if (!value || value.trim() === '') {
				setHasError(true);
				return;
			}
			if (value.length < 2) {
				setHasError(true);
				return;
			}
		}

		// Updated validation for regular hours
		if (name === 'defaultRegularHours' && typeof value === 'string') {
			if (value === '') {
				// Let user clear the field, allow typing again
				setHasError(false);
				OnChange(value, name);
				return;
			}

			// Only validate if the value has content and is in valid format
			const minutes = timeStringToMinutes(value);
			if (minutes !== null && minutes !== undefined) {
				// If minutes > 480 (8 hours), split into regular and overtime
				if (minutes > 480) {
					const regularMinutes = 480; // 8 hours max for regular
					const extraMinutes = minutes - 480; // Extra hours from regular

					// Get current overtime hours and ADD the extra to it
					const currentOvertimeMinutes =
						timeStringToMinutes(
							shiftDetails.defaultOverTimeHours
						) || 0;
					const newOvertimeMinutes =
						currentOvertimeMinutes + extraMinutes;

					// Update both regular and overtime hours
					setShiftDetails((prev: any) => {
						const updated = {
							...prev,
							defaultRegularHours:
								minutesToTimeString(regularMinutes),
							defaultOverTimeHours:
								minutesToTimeString(newOvertimeMinutes),
						};

						// Auto-calculate end time based on start time + regular hours only
						if (updated.startTime) {
							updated.endTime = dayjs(updated.startTime).add(
								regularMinutes,
								'minute'
							);
						}

						return updated;
					});

					setHasError(false);
					return;
				} else if (minutes <= 0) {
					setHasError(true);
					OnChange(value, name);
					return;
				} else {
					setHasError(false);
				}
			}
		}

		if (typeof value === 'string' && regex) {
			const _regex = new RegExp(regex);
			setHasError(!_regex.test(value));
		}

		// Always call OnChange for all fields (including shiftType)
		OnChange(value, name);
	};

	const OnChange = (value: string | number | Dayjs | null, key: string) => {
		setShiftDetails((prev: any) => {
			const updated = {
				...prev,
				[key]: value,
			};

			// Auto-calculate end time when regular hours or start time changes
			if (
				(key === 'defaultRegularHours' || key === 'startTime') &&
				updated.defaultRegularHours &&
				updated.startTime
			) {
				const regularTimeStr =
					typeof updated.defaultRegularHours === 'string'
						? updated.defaultRegularHours
						: '00:00';
				const regularMinutes = timeStringToMinutes(regularTimeStr);

				if (regularMinutes > 0) {
					updated.endTime = dayjs(updated.startTime).add(
						regularMinutes,
						'minute'
					);
				} else {
					updated.endTime = null;
				}
			}

			// Clear end time if regular hours or start time is cleared
			if (key === 'defaultRegularHours' && (!value || value === '')) {
				updated.endTime = null;
			}
			if (key === 'startTime' && !value) {
				updated.endTime = null;
			}
			return updated;
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};

	const validateTimeRange = (
		startTime: Dayjs | null,
		endTime: Dayjs | null
	): boolean => {
		if (!startTime || !endTime) return true;

		const start = startTime.hour() * 60 + startTime.minute();
		const end = endTime.hour() * 60 + endTime.minute();

		// Handle overnight shifts
		if (end <= start) {
			return true; // Overnight shift is valid
		}

		return end > start;
	};

	const handleSubmit = async () => {
		let checkFormError = validateFormData(
			{ ...shiftDetails },
			{ ...formError }
		);

		// Custom validation for required fields
		if (!shiftDetails.shiftName || shiftDetails.shiftName.trim() === '') {
			checkFormError.shiftName = true;
		}
		if (!shiftDetails.shiftType) {
			checkFormError.shiftType = true;
		}
		if (!shiftDetails.startTime) {
			checkFormError.startTime = true;
		}
		if (!shiftDetails.endTime) {
			checkFormError.endTime = true;
		}
		if (
			!shiftDetails.defaultRegularHours ||
			timeStringToMinutes(shiftDetails.defaultRegularHours) <= 0
		) {
			checkFormError.defaultRegularHours = true;
		}

		// Additional validation for regular hours <= 8
		if (shiftDetails.defaultRegularHours) {
			const minutes = timeStringToMinutes(
				shiftDetails.defaultRegularHours
			);
			// Remove the > 8 hours validation since we now auto-split
			if (minutes <= 0) {
				checkFormError.defaultRegularHours = true;
			}
		}

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		}

		// Validate time range
		if (shiftDetails.startTime && shiftDetails.endTime) {
			if (
				!validateTimeRange(shiftDetails.startTime, shiftDetails.endTime)
			) {
				toastText('Invalid time range', 'error');
				return;
			}
		}
		setLoading(true);
		try {
			// Prepare data for submission
			const shiftData = {
				...shiftDetails,
				defaultRegularHours: timeStringToMinutes(
					shiftDetails.defaultRegularHours
				),
				defaultOverTimeHours: timeStringToMinutes(
					shiftDetails.defaultOverTimeHours
				),
			};

			// Call the save function if provided
			if (editSelected) {
				await shiftMasterApi.updateShiftMaster(
					editSelected.id,
					shiftData
				);
			} else {
				await shiftMasterApi.createShiftMaster(shiftData);
			}
			fetchShiftMaster();

			toastText('Shift saved successfully', 'success');
			closeModal();
		} catch (error: any) {
			let message =
				error?.response?.data?.message ||
				(editSelected
					? 'Something went wrong in updating shift'
					: 'Something went wrong in creating shift');
			toastText(message, 'error');
		} finally {
			setLoading(false);
		}
	};

	const closeModal = () => {
		setShiftDetails({
			shiftName: '',
			shiftType: '',
			startTime: null,
			endTime: null,
			defaultRegularHours: '', // Changed to empty string
			defaultOverTimeHours: '00:00',
		});
		setFormError({
			name: false,
			shiftType: false,
			startTime: false,
			endTime: false,
			defaultRegularHours: false,
			defaultOverTimeHours: false,
		});
		closeDrawerByAnimation();
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: loading,
			onclick: () => closeModal(),
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Save',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: loading,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			onclick: () => handleSubmit(),
		},
	];

	const shiftTypeOptions = [
		{ value: 'DAY', label: 'Day Shift' },
		{ value: 'NIGHT', label: 'Night Shift' },
	];

	return (
		<>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicates mandatory fields
			</p>

			<div className={styles['shift-scrollable-content']}>
				<div className={styles['side-drawer-form']}>
					<Row gutter={20}>
						<Col span={24} className={styles['col']}>
							<InputField
								name="shiftName"
								value={shiftDetails.shiftName}
								label="Shift Name"
								required={true}
								helperText="Shift name required"
								onChange={(value) => {
									handleChange(value, 'shiftName', true);
								}}
								isError={formError.shiftName}
								disabled={false}
								isViewOnly={false}
							/>
						</Col>

						{/* Shift Type */}
						<Col span={24} className={styles['col']}>
							<SelectDropdown
								placeholder="Select Shift Type"
								options={shiftTypeOptions}
								value={shiftDetails.shiftType}
								onChange={(value: any) => {
									handleChange(value, 'shiftType', true);
								}}
								size="large"
								required={true}
								helperText="Shift type is required"
								label="Shift Type"
								disabled={editSelected}
								isError={formError.shiftType}
								isViewOnly={false}
							/>
						</Col>

						<Col span={12} className={styles['col']}>
							<InputShiftHoursField
								value={shiftDetails.defaultRegularHours}
								onChange={(value) => {
									handleChange(
										value,
										'defaultRegularHours',
										true
									);
								}}
								disabled={false}
								label="Regular Hours"
								showLabel={true}
								required={true}
								name="defaultRegularHours"
								size="large"
								isError={formError.defaultRegularHours}
								isViewOnly={false}
								style={{ textAlign: 'right' }}
								placeholder="8:00"
								helperText="Regular hours should be greater than 0:00"
							/>
						</Col>

						<Col span={12} className={styles['col']}>
							<InputHoursField
								value={shiftDetails.defaultOverTimeHours}
								onChange={(value) => {
									handleChange(
										value,
										'defaultOverTimeHours',
										false
									);
								}}
								disabled={false}
								label="Overtime Hours"
								showLabel={true}
								required={false}
								name="defaultOverTimeHours"
								size="large"
								isError={formError.defaultOverTimeHours}
								isViewOnly={false}
								style={{ textAlign: 'right' }}
							/>
						</Col>

						{/* Start Time - Second input */}
						<Col span={12} className={styles['col']}>
							<TimePickerField
								name="startTime"
								value={shiftDetails.startTime || dayjs()}
								label="Shift Start Time"
								required={true}
								helperText="Shift start time required"
								onChange={(value) => {
									handleChange(
										dayjs(value),
										'startTime',
										true
									);
								}}
								isError={formError.startTime}
								disabled={false} // Always enabled now
								isViewOnly={false}
							/>
						</Col>

						{/* End Time - Auto-calculated and disabled */}
						<Col span={12} className={styles['col']}>
							<TimePickerField
								name="endTime"
								value={shiftDetails.endTime || dayjs()}
								label="Shift End Time"
								required={true}
								onChange={(value) => {
									handleChange(dayjs(value), 'endTime', true);
								}}
								isError={false}
								disabled={true}
								isViewOnly={false}
								startTime={shiftDetails.startTime || dayjs()}
								placeholder="Auto-calculated"
							/>
						</Col>
					</Row>
					{/* Buttons */}
					<div className={styles['side-drawer-form__buttons']}>
						<Buttons buttons={buttons} />
					</div>
				</div>
			</div>
		</>
	);
};

export default AddShiftBody;
